import { z } from 'zod';
import type { BerichtSummen, BerichtTag } from './berichte';
import type { KampagneTyp } from './kampagnen';
import type { DashboardZeitraum } from './werbeDashboard';

/**
 * Retail Media in der DXL-Plattform: Dashboard über alle Marktplätze (Werbeumsatz, Verlauf, Formate, Top-Marktplätze,
 * Hinweise), Liste aller Marktplätze mit Modulstand, Modul je Marktplatz ein- und ausschalten, Dashboard eines
 * Marktplatzes. Gelesen werden nur kleine Tabellen (Werbekonten, Guthaben, Kampagnen, Buchungen) und der Tages-Rollup.
 */

export const WerbungPlattformAbfrage = z.object({
  tage: z.coerce.number().int().refine((t): t is DashboardZeitraum => t === 7 || t === 30 || t === 90, 'Zeitraum 7, 30 oder 90 Tage').default(30),
  /** Keyset: Kürzel des letzten Marktplatzes der vorigen Seite */
  nach: z.string().trim().min(1).max(12).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const WerbungSchalten = z.object({ aktiv: z.boolean() });
export type WerbungSchalten = z.infer<typeof WerbungSchalten>;

export interface WerbungPlattformZeile {
  id: string;
  kuerzel: string;
  name: string;
  status: 'aktiv' | 'eingeladen' | 'gesperrt';
  /** Modul werbung gesetzt */
  aktiv: boolean;
  /** nur Retail Media (Shop ohne Marktplatz) – das Modul lässt sich dort nicht ausschalten */
  nurWerbung: boolean;
  mollieEingerichtet: boolean;
  stripeEingerichtet: boolean;
  werbekonten: number;
  kampagnenAktiv: number;
  guthabenCent: number;
  werbemittelPruefung: number;
  /** im Zeitraum (Rollup) */
  klicks: number;
  kostenCent: number;
  umsatzCent: number;
  /** bestätigte WKZ-Buchungen mit Beginn im Zeitraum */
  wkzCent: number;
}

export interface WerbungPlattform {
  tage: DashboardZeitraum;
  von: string;
  bis: string;
  summen: BerichtSummen & {
    marktplaetze: number;
    aktiv: number;
    /** Marktplätze mit Modul und mindestens einem Zahlungsweg (Stripe oder Mollie) */
    mitZahlungsweg: number;
    werbekonten: number;
    kampagnenAktiv: number;
    guthabenCent: number;
    wkzCent: number;
  };
  /** gleich langer Zeitraum davor – für die Trends */
  vorher: BerichtSummen & { wkzCent: number };
  reihe: BerichtTag[];
  formate: { format: KampagneTyp; klicks: number; kostenCent: number; umsatzCent: number }[];
  /** Marktplätze mit Modul, aber ohne Zahlungsweg (keine Aufladung möglich); Prüfungen und Anfragen über alle Marktplätze */
  hinweise: {
    ohneZahlungsweg: number; werbemittelPruefung: number; wkzAngefragt: number; ohneWerbekonto: number;
    /** Werbekonten über alle Marktplätze, deren Abbuchung fehlgeschlagen ist */
    abbuchungFehlgeschlagen: number;
  };
  /** nach Werbeumsatz (Klicks + WKZ) im Zeitraum, höchstens 5 */
  top: WerbungPlattformZeile[];
  /** Seite der Liste aller Marktplätze, nach Kürzel */
  zeilen: WerbungPlattformZeile[];
  /** Kürzel für die nächste Seite, null = Ende */
  weiter: string | null;
}
